import React, { useState, useRef, useEffect } from "react";
import { useLang } from "./LangContext";
import { useTenant } from "./TenantContext";
import { MessageCircle, X, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
    const { t } = useLang();
    const { slug } = useTenant();
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [messages, setMessages] = useState([]);
    const [sessionId] = useState(() => "sess-" + Math.random().toString(36).slice(2, 12));
    const scrollRef = useRef(null);

    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([{ role: "assistant", content: t.chat_welcome }]);
        }
    }, [open, t.chat_welcome]); // eslint-disable-line

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    const send = async () => {
        const q = input.trim();
        if (!q || sending) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", streaming: true }]);
        setSending(true);
        try {
            const res = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: q, session_id: sessionId, borgo: slug }),
            });
            if (!res.ok || !res.body) throw new Error("bad response");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                acc += decoder.decode(value, { stream: true });
                setMessages((m) => {
                    const copy = [...m];
                    copy[copy.length - 1] = { role: "assistant", content: acc, streaming: true };
                    return copy;
                });
            }
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: acc };
                return copy;
            });
        } catch (e) {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "⚠️ " + (e.message || "error") };
                return copy;
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="floating-chat-btn pulse-ring fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 w-14 h-14 md:w-16 md:h-16 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center shadow-2xl transition-colors"
                    data-testid="chatbot-open-btn"
                    aria-label="Open chatbot"
                >
                    <MessageCircle size={26} />
                </button>
            )}

            {open && (
                <div
                    className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 w-[calc(100vw-3rem)] sm:w-[400px] max-w-md h-[540px] bg-white border border-stone-200 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
                    data-testid="chatbot-window"
                >
                    <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-br from-emerald-700 to-emerald-600 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                                <MessageCircle size={18} />
                            </div>
                            <div>
                                <div className="font-serif text-lg font-semibold leading-tight">{t.chat_title}</div>
                                <div className="text-xs text-emerald-100">Gemini · online</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
                            data-testid="chatbot-close-btn"
                            aria-label="Close chatbot"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3 bg-stone-50">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-emerald-600 text-white rounded-br-sm"
                                            : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"
                                    } ${m.streaming ? "streaming-caret" : ""}`}
                                    data-testid={`chat-msg-${m.role}-${i}`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="px-4 py-3 border-t border-stone-200 bg-white flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            placeholder={t.chat_placeholder}
                            className="flex-1 px-4 py-2.5 rounded-full border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm bg-stone-50"
                            data-testid="chatbot-input"
                            disabled={sending}
                        />
                        <button
                            onClick={send}
                            disabled={sending || !input.trim()}
                            className="w-10 h-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid="chatbot-send-btn"
                            aria-label="Send"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
